import { useCallback, useEffect, useState } from 'react'
import { Sparkles, X, RefreshCw, Clock, AlertCircle } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

/**
 * "State of your cyber world" exec briefing.
 * Hits /api/cyber/narrative, which grounds a DeepSeek prompt in our own data
 * (CVE archive, KEV flips, ransomware victims, threat-actor heat, vendor
 * advisories, cable faults) and returns a 2–3 paragraph markdown narrative.
 * Falls back to a deterministic stub if the LLM key isn't set.
 */
export default function NarrativeModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [days, setDays] = useState(7)

  const fetchNarrative = useCallback(async (force = false) => {
    setLoading(true)
    try {
      const r = await api.get('/api/cyber/narrative', { params: { days, force: force ? 'true' : undefined } })
      setData(r.data)
    } catch (e) {
      setData({ available: false, reason: 'Request failed', fallback: 'Narrative temporarily unavailable.' })
    } finally { setLoading(false) }
  }, [days])

  useEffect(() => { if (open) fetchNarrative(false) }, [open, fetchNarrative])

  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape' && open) onClose() }
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [open, onClose])

  if (!open) return null

  const text = data?.narrative || data?.fallback || ''

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[8vh] bg-black/60 backdrop-blur-sm animate-fade-in" onClick={onClose}>
      <div className="card w-full max-w-3xl mx-4 shadow-2xl max-h-[84vh] flex flex-col border border-border-light" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-blue" />
            <h2 className="text-sm font-semibold text-text-primary">State of your cyber world</h2>
            <span className="text-[10px] text-text-muted">· last {days}d</span>
            {data?.model && <span className="text-[9px] font-mono text-text-muted px-1.5 py-0.5 rounded bg-surface-hover">{data.model}</span>}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-0 rounded border border-border overflow-hidden text-[10px]">
              {[3, 7, 14, 30].map(d => (
                <button key={d} type="button" onClick={() => setDays(d)}
                  className={clsx('px-2 py-0.5', days === d ? 'bg-blue/20 text-blue-light font-semibold' : 'text-text-muted hover:bg-surface-hover')}>
                  {d}d
                </button>
              ))}
            </div>
            <button type="button" onClick={() => fetchNarrative(true)} className="btn-ghost p-1" title="Force regenerate">
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
            </button>
            <button type="button" onClick={onClose} className="btn-ghost p-1" title="Close (Esc)">
              <X size={14} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && !text && (
            <div className="text-center py-12 text-text-muted text-sm">
              <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
              Composing narrative…
            </div>
          )}
          {!data?.available && data?.reason && (
            <div className="mb-3 p-2 rounded border border-amber/30 bg-amber/5 text-[11px] text-amber-light flex items-start gap-2">
              <AlertCircle size={11} className="mt-0.5 flex-shrink-0" />
              <div>{data.reason}. Showing deterministic fallback below.</div>
            </div>
          )}
          {text && <MarkdownSections text={text} />}
        </div>

        <div className="px-5 py-2 border-t border-border text-[10px] text-text-muted flex items-center justify-between">
          <span className="flex items-center gap-1">
            <Clock size={10} /> Generated {data?.generated_at ? new Date(data.generated_at).toLocaleTimeString() : '—'} · cached 1h
          </span>
          <span>Grounded in {data?.facts?.cve_stats?.total?.toLocaleString?.() || '—'} CVE archive + live feeds</span>
        </div>
      </div>
    </div>
  )
}

/**
 * Very small markdown renderer — enough for our DeepSeek output (## headings,
 * paragraphs, and `- bullets`). We don't want another npm dep just for this.
 */
function MarkdownSections({ text }: { text: string }) {
  const blocks: React.ReactNode[] = []
  let currentList: string[] = []
  const flushList = () => {
    if (currentList.length) {
      blocks.push(
        <ul key={`ul-${blocks.length}`} className="list-disc pl-6 mb-3 space-y-1 text-sm text-text-secondary">
          {currentList.map((li, i) => <li key={i}>{renderInline(li)}</li>)}
        </ul>
      )
      currentList = []
    }
  }
  for (const raw of text.split('\n')) {
    const line = raw.trim()
    if (!line) { flushList(); continue }
    if (line.startsWith('## ')) {
      flushList()
      blocks.push(<h3 key={`h-${blocks.length}`} className="text-base font-semibold text-text-primary mt-4 mb-2 first:mt-0">{line.replace(/^##\s*/, '')}</h3>)
    } else if (line.startsWith('# ')) {
      flushList()
      blocks.push(<h2 key={`h-${blocks.length}`} className="text-lg font-bold text-text-primary mt-4 mb-2">{line.replace(/^#\s*/, '')}</h2>)
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      currentList.push(line.replace(/^[-*]\s*/, ''))
    } else {
      flushList()
      blocks.push(<p key={`p-${blocks.length}`} className="text-sm text-text-secondary leading-relaxed mb-3">{renderInline(line)}</p>)
    }
  }
  flushList()
  return <>{blocks}</>
}

function renderInline(s: string): React.ReactNode {
  // Bold **text**, code `text`, and CVE-ID highlight
  const parts: React.ReactNode[] = []
  let remaining = s
  const patterns: { re: RegExp; wrap: (m: string) => React.ReactNode }[] = [
    { re: /\*\*(.+?)\*\*/, wrap: m => <b className="text-text-primary">{m}</b> },
    { re: /`(.+?)`/,       wrap: m => <code className="text-blue-light bg-surface-hover px-1 rounded font-mono text-[12px]">{m}</code> },
    { re: /\b(CVE-\d{4}-\d{4,7})\b/, wrap: m => <span className="font-mono text-blue-light">{m}</span> },
  ]
  let guard = 0
  while (remaining && guard++ < 50) {
    let matched = false
    for (const p of patterns) {
      const m = remaining.match(p.re)
      if (m) {
        const before = remaining.slice(0, m.index!)
        if (before) parts.push(before)
        parts.push(<span key={parts.length}>{p.wrap(m[1])}</span>)
        remaining = remaining.slice((m.index || 0) + m[0].length)
        matched = true
        break
      }
    }
    if (!matched) { parts.push(remaining); break }
  }
  return <>{parts}</>
}
